<template>
  <q-field
    ref="field"
    :model-value="modelValue"
    outlined
    :disable="disable"
    :hint="hint"
    :error="error"
    :error-message="errorMessage"
    :label="label"
    :dense="dense"
    :clearable="clearable && !mobileMode"
    stack-label
    hide-bottom-space
    class="cursor-pointer"
    @focus="onFocus"
    @clear="setZero"
  >
    <template #control>
      <span>{{ minutesToStr(modelValue, nullLabel, zeroLabel, withSuffixLabel ? currentOffsetStr : null) }}</span>
    </template>

    <template #append>
      <q-icon name="mdi-menu-down" />
    </template>

    <responsive-menu
      v-if="!disable"
      ref="menu"
      card-style="min-width: 350px;"
      :breakpoint="1024"
      :toolbar-title="mobileLabel"
      no-parent-event="desktop"
      :no-refocus="true"
    >
      <q-field
        :model-value="modelValue"
        outlined
        class="borderless-children q-pa-sm"
        :clearable="clearable"
        stack-label
        dense
        hide-bottom-space
        color="blue-grey"
        @clear="setZero"
        @focus="onCustomFocus"
        @blur="onCustomBlur"
      >
        <div class="flex full-width no-wrap">
          <q-input
            v-model="selected.day"
            type="number"
            :min="0"
            :max="700"
            :label="pluraliseString('Day', selected.day)"
            borderless
            dense
            class="hideable-field"
            :class="{'visible': showDayField}"
            @focusout="setTimeInMinutes"
            @update:model-value="hasMadeChange = true"
          />
          <q-input
            v-model="selected.hour"
            type="number"
            :min="0"
            :max="23"
            :label="pluraliseString('Hour', selected.hour)"
            outlined
            borderless
            dense
            class="hideable-field"
            :class="{'visible': showHourField}"
            @focusout="setTimeInMinutes"
            @update:model-value="hasMadeChange = true"
          />
          <q-input
            v-model="selected.minute"
            type="number"
            :min="0"
            :max="59"
            :label="pluraliseString('Minute', selected.minute)"
            outlined
            borderless
            dense
            class="hideable-field"
            :class="{'visible': showMinuteField}"
            @focusout="setTimeInMinutes"
            @update:model-value="hasMadeChange = true"
          />
          <q-select
            v-if="!limitOffset"
            :model-value="currentOffset"
            :options="offsetOptions"
            :disable="!modelValue"
            outlined
            borderless
            dense
            behavior="menu"
            @update:model-value="setOffset($event), hasMadeChange = true"
          />
        </div>
      </q-field>

      <div v-if="defaultTimerOptions.length > 0">
        <q-item-label header class="text-bold q-mt-md">
          Default Values
        </q-item-label>
        <q-list dense>
          <q-item
            v-for="(item, key) in defaultTimerOptions"
            :key="key"
            :active="modelValue === item.value"
            clickable
            @click="selectDefaultValue(item.value)"
          >
            <q-item-section>
              {{ item.label }}
            </q-item-section>
          </q-item>
        </q-list>
      </div>
    </responsive-menu>
  </q-field>
</template>

<script>
/**
 * MinutePicker acts similar to default QInput component, including props
 * But it allows the user to select "minutes beforehand"
 */
export default {
  props: {
    modelValue: {
      type: Number,
      default: 0
    },
    hint: {
      type: String
    },
    error: {
      default: false,
      type: Boolean
    },
    errorMessage: {
      type: String
    },
    label: {
      type: String
    },
    disable: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    // Minimum offset which is allowed (works for positive and negative - value of 15 means range is: x <= -15 & x >= 15
    minOffset: {
      type: Number,
      default: 0
    },
    // Label for minutes set as 0
    zeroLabel: {
      type: String,
      default: 'At time of appointment'
    },
    // Label for minutes being unset (null)
    nullLabel: {
      type: String,
      default: 'None'
    },
    // Display the suffix label after the specified formatted time (before, after)
    withSuffixLabel: {
      type: Boolean,
      default: true
    },
    // [null (any), 'before' (negative values), 'after' (positive values)] : limit how offset options can be chosen
    limitOffset: {
      type: String,
      default: 'before'
    },
    // Label to show on mobile devices within the popup
    mobileLabel: {
      type: String,
      default: 'Select Timer'
    },
    beforeStr: {
      type: String,
      default: 'before'
    },
    afterStr: {
      type: String,
      default: 'after'
    },
    // Custom default intervals to show
    options: {
      type: Array,
      default: () => {
        return [-15, -30, -60, -120, -720, -1440, -10080, -20160, -43200]
      }
    }
  },
  emits: ['update:modelValue', 'clear'],
  data () {
    return {
      focused: false,
      hasMadeChange: false,
      selected: {
        day: 0,
        hour: 0,
        minute: 0
      },
      offsetOptions: [
        'before',
        'after'
      ]
    }
  },
  computed: {
    negMinOffset () {
      return this.minOffset * -1
    },
    currentOffset () {
      if (!this.modelValue && this.limitOffset === 'before') {
        return 'before'
      }
      return (!this.modelValue || this.modelValue >= 0) ? 'after' : 'before'
    },
    currentOffsetStr () {
      return this.currentOffset === 'before' ? this.beforeStr : this.afterStr
    },
    defaultTimerOptions () {
      const arr = [null, 0]
      arr.push(...this.options)

      return arr
        .filter(item => {
          if ((!this.clearable && item === null) || (item === 0 && this.minOffset > 0)) {
            return false
          }
          return !(item !== null &&
            (
              (item > 0 && (this.limitOffset === 'before' || item < this.minOffset)) ||
              (item < 0 && (this.limitOffset === 'after' || item > this.negMinOffset))
            )
          )
        })
        .map(item => {
          return {
            value: item,
            label: this.minutesToStr(item, this.nullLabel, this.zeroLabel, this.withSuffixLabel ? (item >= 0 ? 'after' : 'before') : null)
          }
        })
    },
    mobileMode () {
      return this.windowWidth <= 1024 || this.$q.platform.is.mobile
    },
    showDayField () {
      return this.focused || this.selected.day > 0
    },
    showHourField () {
      return this.focused || this.selected.hour > 0 || (this.selected.day > 0 && this.selected.minute > 0)
    },
    showMinuteField () {
      return this.focused || (this.selected.hour < 1 && this.selected.day < 1) || this.selected.minute > 0
    }
  },
  watch: {
    'modelValue' (val) {
      this.convertMinute(val)
    }
  },
  methods: {
    setTimeInMinutes () {
      // hasMadeChange will prevent the picker from changing null to 0 if opening and closing the picker
      // without making any changes
      if (!this.hasMadeChange) {
        return
      }

      let minutes = parseInt(this.selected.minute) + parseInt(this.selected.hour * 60) + parseInt((this.selected.day < 700 ? this.selected.day : 700) * 1440)
      // Make sure the value is always in the correct state based on the current offset type
      minutes = Math.abs(minutes) * (this.currentOffset === 'before' ? -1 : 1)

      // Handle all the use cases of positive and negative numbers against the minimum offset
      if (minutes === 0 && this.minOffset > 0) {
        minutes = this.limitOffset === 'before' ? this.negMinOffset : this.minOffset
      }
      else if (minutes > 0) {
        if (this.limitOffset === 'before') {
          minutes = this.negMinOffset
        }
        else if (minutes < this.minOffset) {
          minutes = this.minOffset
        }
      }
      else if (minutes < 0) {
        if (this.limitOffset === 'after') {
          minutes = this.minOffset
        }
        else if (minutes > this.negMinOffset) {
          minutes = this.negMinOffset
        }
      }
      this.$emit('update:modelValue', minutes)
      this.hasMadeChange = false
    },
    convertMinute (val) {
      val = val || 0

      // Handle all the use cases of positive and negative numbers against the minimum offset
      if (val === 0 && this.minOffset > 0) {
        val = this.limitOffset === 'before' ? this.negMinOffset : this.minOffset
      }
      else if (val > 0) {
        if (this.limitOffset === 'before') {
          val = this.negMinOffset
        }
        else if (val < this.minOffset) {
          val = this.minOffset
        }
      }
      else if (val < 0) {
        if (this.limitOffset === 'after') {
          val = this.minOffset
        }
        else if (val > this.negMinOffset) {
          val = this.negMinOffset
        }
      }
      const luxonDuration = this.luxonDuration.fromObject({ minutes: Math.abs(val) })
        .shiftTo('days', 'hours', 'minutes')
        .toObject()
      this.selected.day = Math.floor(luxonDuration.days)
      this.selected.hour = luxonDuration.hours
      this.selected.minute = luxonDuration.minutes
    },
    selectDefaultValue (val) {
      if (val === null) {
        this.setZero()
      }
      else {
        this.$emit('update:modelValue', val)
      }

      // Close popup after clicking result
      this.timed(() => {
        this.onBlur()
      }, 250)
    },
    setOffset (val) {
      if (val === 'after') {
        this.$emit('update:modelValue', Math.abs(this.modelValue))
      }
      else {
        this.$emit('update:modelValue', this.modelValue * -1)
      }
    },
    setZero () {
      if (this.$refs.menu) {
        this.$refs.menu.close()
      }
      this.convertMinute(null)
      this.$emit('update:modelValue', null)
      this.$emit('clear')
    },
    onFocus () {
      this.hasMadeChange = false
      if (this.$refs.menu) {
        this.$refs.menu.open()
      }
    },
    onBlur () {
      if (this.$refs.menu) {
        this.$refs.menu.close()
      }
      this.focused = false
      this.setTimeInMinutes()
    },
    onCustomFocus () {
      this.focused = true
    },
    onCustomBlur () {
      this.focused = false
    },
    focus () {
      this.$refs.field.focus()
    }
  },
  mounted () {
    this.convertMinute(this.modelValue)
  }
}
</script>

<style scoped lang="scss">
.hideable-field {
  transition: opacity 0.25s ease;
  opacity: 0.25;
  width: 75px !important;

  &.visible {
    opacity: 1 !important;
  }
}
</style>
